import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Btn, Input, Select, Modal, Card, Badge, EmptyState, fmtDate } from "@/components/ui-kit";
import { Plus, Trash2, Shield, KeyRound, RotateCcw, Check, X } from "lucide-react";

const emptyUser = { email: "", password: "", name: "", role: "sales_rep", phone: "", territory: "", permissions: null };

export default function Users() {
  const { t } = useLang();
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [catalog, setCatalog] = useState({ permissions: [], role_defaults: {} });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("basic"); // basic / permissions

  const load = async () => {
    const [u, c] = await Promise.all([api.get("/users"), api.get("/permissions/catalog")]);
    setList(u.data); setCatalog(c.data);
  };
  useEffect(() => { load(); }, []);

  // Group permissions by module
  const grouped = useMemo(() => {
    const m = {};
    for (const p of catalog.permissions) {
      if (!m[p.module]) m[p.module] = [];
      m[p.module].push(p);
    }
    return m;
  }, [catalog]);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...emptyUser, permissions: catalog.role_defaults["sales_rep"] || [] });
    setTab("basic");
    setOpen(true);
  };

  const startEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email, password: "", name: u.name, role: u.role,
      phone: u.phone || "", territory: u.territory || "",
      permissions: u.permissions || catalog.role_defaults[u.role] || [],
    });
    setTab("basic");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (editing) {
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/auth/register", payload);
      }
      setOpen(false);
      await load();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const disable = async (id) => {
    if (!window.confirm("Disable user? They will not be able to login.")) return;
    await api.delete(`/users/${id}`);
    await load();
  };

  const toggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { active: !u.active });
    await load();
  };

  // Permission matrix helpers
  const isChecked = (key) => (form.permissions || []).includes(key);
  const toggle = (key) => {
    const set = new Set(form.permissions || []);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    setForm({ ...form, permissions: Array.from(set) });
  };
  const toggleModule = (modulePerms, on) => {
    const set = new Set(form.permissions || []);
    for (const p of modulePerms) {
      if (on) set.add(p.key); else set.delete(p.key);
    }
    setForm({ ...form, permissions: Array.from(set) });
  };
  const applyRoleDefaults = (role) => {
    const def = catalog.role_defaults[role] || [];
    setForm({ ...form, role, permissions: [...def] });
  };

  const roleColor = (r) => r === "admin" ? "blue" : r === "supervisor" ? "purple" : "slate";

  return (
    <div data-testid="users-page">
      <PageHeader
        title={t("users")}
        subtitle="Team management with granular permissions"
        actions={<Btn onClick={startCreate} data-testid="add-user-btn"><Plus className="w-4 h-4"/>{t("add")}</Btn>}
      />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800 border-y border-slate-200 dark:border-slate-700 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <th className="py-2 px-3 text-start">{t("name")}</th>
                <th className="py-2 px-3 text-start">{t("email")}</th>
                <th className="py-2 px-3 text-start">{t("role")}</th>
                <th className="py-2 px-3 text-start">Permissions</th>
                <th className="py-2 px-3 text-start">Territory</th>
                <th className="py-2 px-3 text-start">{t("status")}</th>
                <th className="py-2 px-3 text-end">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map(u => {
                const permCount = (u.permissions || catalog.role_defaults[u.role] || []).length;
                const isCustom = u.permissions && JSON.stringify(u.permissions.sort()) !== JSON.stringify((catalog.role_defaults[u.role] || []).slice().sort());
                return (
                  <tr key={u.id} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 px-3 font-semibold text-slate-900 dark:text-white">{u.name}</td>
                    <td className="py-2 px-3 text-xs text-slate-600 dark:text-slate-300">{u.email}</td>
                    <td className="py-2 px-3"><Badge color={roleColor(u.role)}>{t(u.role)}</Badge></td>
                    <td className="py-2 px-3">
                      <span className="inline-flex items-center gap-1.5">
                        <Shield className="w-3 h-3 text-slate-400"/>
                        <span className="text-xs font-mono">{permCount}/{catalog.permissions.length}</span>
                        {isCustom && <Badge color="amber">custom</Badge>}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs">{u.territory || "-"}</td>
                    <td className="py-2 px-3">
                      <button onClick={() => toggleActive(u)} disabled={u.id === me.id} className="disabled:opacity-50">
                        <Badge color={u.active !== false ? "green" : "red"}>{u.active !== false ? "Active" : "Disabled"}</Badge>
                      </button>
                    </td>
                    <td className="py-2 px-3 text-end">
                      <div className="inline-flex gap-1">
                        <button onClick={() => startEdit(u)} className="text-xs font-semibold text-blue-700 dark:text-blue-400 px-2 py-1 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded" data-testid={`edit-user-${u.id}`}>Edit</button>
                        {u.id !== me.id && (
                          <button onClick={() => disable(u.id)} className="p-1 hover:bg-red-50 dark:hover:bg-red-950/40 rounded" data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="w-4 h-4 text-red-600"/>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {list.length === 0 && <EmptyState message={t("noData")}/>}
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Edit ${editing.name}` : "Add Team Member"} maxWidth="max-w-3xl">
        <form onSubmit={submit} data-testid="user-form">
          {/* Tabs */}
          <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700 mb-4">
            {[
              { key: "basic", label: "Basic Info", icon: KeyRound },
              { key: "permissions", label: `Permissions (${(form.permissions || []).length}/${catalog.permissions.length})`, icon: Shield },
            ].map(t => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`user-tab-${t.key}`}
                className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors inline-flex items-center gap-2 ${tab === t.key ? "border-blue-700 text-blue-700 dark:text-blue-400" : "border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
                <t.icon className="w-4 h-4"/>{t.label}
              </button>
            ))}
          </div>

          {tab === "basic" && (
            <div className="space-y-3">
              <Input label="Name *" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="user-name"/>
              <Input label="Email *" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="user-email" disabled={!!editing}/>
              <Input label={editing ? "New Password (leave blank to keep)" : "Password *"} type="password" required={!editing} minLength={editing ? 0 : 6} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="user-password"/>
              <div className="grid grid-cols-2 gap-3">
                <Select label="Role *" value={form.role} onChange={(e) => applyRoleDefaults(e.target.value)} data-testid="user-role">
                  <option value="sales_rep">Sales Rep</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="admin">Admin</option>
                </Select>
                <Input label="Phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}/>
                <Input label="Territory" value={form.territory} onChange={(e) => setForm({...form, territory: e.target.value})} className="col-span-2"/>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded p-3">
                💡 Changing the <b>Role</b> will apply that role's default permissions. You can fine-tune them in the <b>Permissions</b> tab.
              </div>
            </div>
          )}

          {tab === "permissions" && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-slate-200 dark:border-slate-700">
                <span className="overline">Apply preset:</span>
                {["admin", "supervisor", "sales_rep"].map(r => (
                  <button key={r} type="button" onClick={() => setForm({...form, permissions: [...(catalog.role_defaults[r] || [])]})}
                    className="text-xs font-semibold px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:text-blue-700">
                    <RotateCcw className="w-3 h-3 inline me-1"/>{r.replace("_", " ")} default
                  </button>
                ))}
                <button type="button" onClick={() => setForm({...form, permissions: []})}
                  className="text-xs font-semibold px-3 py-1 rounded-full bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 hover:bg-red-100 ms-auto">
                  Clear all
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
                {Object.entries(grouped).map(([module, perms]) => {
                  const allOn = perms.every(p => isChecked(p.key));
                  const someOn = perms.some(p => isChecked(p.key));
                  return (
                    <div key={module} className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-slate-50/40 dark:bg-slate-900/40">
                      <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-200 dark:border-slate-700">
                        <h4 className="font-bold text-sm text-slate-900 dark:text-white">{module}</h4>
                        <button type="button" onClick={() => toggleModule(perms, !allOn)}
                          className={`text-xs font-semibold px-2 py-0.5 rounded ${allOn ? "bg-blue-700 text-white" : someOn ? "bg-amber-100 text-amber-700" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>
                          {allOn ? "All" : someOn ? "Partial" : "None"}
                        </button>
                      </div>
                      <div className="space-y-1.5">
                        {perms.map(p => (
                          <label key={p.key} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-white dark:hover:bg-slate-800 rounded px-1 py-0.5">
                            <input type="checkbox" checked={isChecked(p.key)} onChange={() => toggle(p.key)}
                              data-testid={`perm-${p.key}`}
                              className="mt-0.5 rounded text-blue-700 focus:ring-blue-600"/>
                            <span className="flex-1">
                              <span className="text-slate-800 dark:text-slate-200">{p.label}</span>
                              <code className="block text-[10px] font-mono text-slate-400">{p.key}</code>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>{t("cancel")}</Btn>
            <Btn type="submit" disabled={busy} data-testid="user-submit">{busy ? t("loading") : t("save")}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
